/**
 * GS Radio poker chip set: raster art in `public/media/chip*.png` (see `chipPngUrl`).
 * Sheet reference: /media/gsradio-poker-chip-set.png. `chipHue` still matches SVG fallback in `GsRadioChipSvg.js`.
 * Denominations: 1, 5, 10, 25, 100, 500, 1K, 5K, 25K, 100K — greedy stack uses largest first.
 */
export const CHIP_DENOMS = [100000, 25000, 5000, 1000, 500, 100, 25, 10, 5, 1];

/** @param {number} v denomination */
export function chipPngBasename(v) {
  if (v >= 100000) return 'chip100k';
  if (v >= 25000) return 'chip25k';
  if (v >= 5000) return 'chip5k';
  if (v >= 1000) return 'chip1k';
  if (v >= 500) return 'chip500';
  if (v >= 100) return 'chip100';
  if (v >= 25) return 'chip25';
  if (v >= 10) return 'chip10';
  if (v >= 5) return 'chip5';
  return 'chip1';
}

/**
 * Absolute URL for chip PNG under `public/media/` (Create React App `PUBLIC_URL`).
 * @param {number} v
 * @returns {string}
 */
export function chipPngUrl(v) {
  const base = process.env.PUBLIC_URL || '';
  return `${base}/media/${chipPngBasename(v)}.png`;
}

/**
 * Greedy break into ≤ maxChips tokens for stack / flight animation.
 * @param {number} amount
 * @param {number} [maxChips=10]
 * @returns {number[]}
 */
export function amountToChipValues(amount, maxChips = 10) {
  let left = Math.max(0, Math.floor(amount));
  const out = [];
  for (const d of CHIP_DENOMS) {
    while (left >= d && out.length < maxChips) {
      out.push(d);
      left -= d;
    }
  }
  while (left > 0 && out.length < maxChips) {
    out.push(1);
    left -= 1;
  }
  return out;
}

/**
 * Greedy change-making, grouped by denomination: each entry is one visual stack of identical chips.
 * Stacks of the same denomination (e.g. 15×100) become multiple columns of at most `maxPerStack` chips.
 * @param {number} amount
 * @param {number} [maxPerStack=10]
 * @returns {{ denom: number, count: number }[]}
 */
export function amountToDenomStacks(amount, maxPerStack = 10) {
  const cap = Math.max(1, Math.floor(maxPerStack));
  let left = Math.max(0, Math.floor(amount));
  const stacks = [];
  for (const d of CHIP_DENOMS) {
    const n = Math.floor(left / d);
    if (n <= 0) continue;
    left -= n * d;
    let rem = n;
    while (rem > 0) {
      const c = Math.min(cap, rem);
      stacks.push({ denom: d, count: c });
      rem -= c;
    }
  }
  return stacks;
}

/** @param {number} v */
export function formatChipLabel(v) {
  if (v >= 1000) return `${Math.round(v / 1000)}K`;
  return String(v);
}

/**
 * Rim / edge / center colors per denomination (GS Radio chip spec).
 * @param {number} v
 * @returns {{ border: string, face: string, stripe: string, center: string }}
 */
export function chipHue(v) {
  if (v >= 100000) {
    return { border: '#0f766e', face: '#14b8a6', stripe: '#ffffff', center: '#0a1628' };
  }
  if (v >= 25000) {
    return { border: '#c2410c', face: '#ea580c', stripe: '#ffffff', center: '#0a1628' };
  }
  if (v >= 5000) {
    return { border: '#be185d', face: '#ec4899', stripe: '#ffffff', center: '#0a1628' };
  }
  if (v >= 1000) {
    return { border: '#a16207', face: '#eab308', stripe: '#ffffff', center: '#0a1628' };
  }
  if (v >= 500) {
    return { border: '#6b21a8', face: '#9333ea', stripe: '#ffffff', center: '#0a1628' };
  }
  if (v >= 100) {
    return { border: '#171717', face: '#262626', stripe: '#e5e5e5', center: '#0a1628' };
  }
  if (v >= 25) {
    return { border: '#15803d', face: '#22c55e', stripe: '#ffffff', center: '#0a1628' };
  }
  if (v >= 10) {
    return { border: '#1d4ed8', face: '#3b82f6', stripe: '#ffffff', center: '#0a1628' };
  }
  if (v >= 5) {
    return { border: '#b91c1c', face: '#ef4444', stripe: '#ffffff', center: '#0a1628' };
  }
  return { border: '#475569', face: '#94a3b8', stripe: '#ffffff', center: '#0a1628' };
}
